import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, TableShell, Field, Drawer } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Copy, Mail } from "lucide-react";

const empty = { name: "", contact_name: "", contact_email: "", phone: "", address: "" };
const inviteEmpty = { company_id: "", email: "", name: "" };
const fmtDeletedDate = (iso) => (iso ? String(iso).slice(0, 10) : "");

export default function Companies() {
  const { user } = useAuth();
  const isAdmin = user?.role === "agency_admin";
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  // Invite drawer state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState(inviteEmpty);
  const [inviteResult, setInviteResult] = useState(null);
  const [invites, setInvites] = useState([]);

  const load = () => api.get(`/companies${showDeleted ? "?include_deleted=true" : ""}`).then((r) => setRows(r.data));
  const loadInvites = () => api.get("/invites").then((r) => setInvites(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [showDeleted]);
  useEffect(() => { loadInvites(); }, []);

  const onEdit = (r) => { setForm({ ...empty, ...r }); setEditingId(r.id); setOpen(true); };
  const onNew = () => { setForm(empty); setEditingId(null); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, contact_email: form.contact_email || null };
      if (editingId) await api.put(`/companies/${editingId}`, payload);
      else await api.post("/companies", payload);
      toast.success(editingId ? "Company updated" : "Company added");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this company? This can be restored by an admin from Show deleted.")) return;
    await api.delete(`/companies/${id}`);
    toast.success("Deleted");
    load();
  };

  const restore = async (id) => {
    try {
      await api.post(`/companies/${id}/restore`);
      toast.success("Restored");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openInvite = (company) => {
    setInviteResult(null);
    setInviteForm({ company_id: company.id, email: company.contact_email || "", name: company.contact_name || "" });
    setInviteOpen(true);
  };

  const sendInvite = async () => {
    try {
      const { data } = await api.post("/invites", {
        company_id: inviteForm.company_id,
        email: inviteForm.email,
        name: inviteForm.name || null,
      });
      setInviteResult(data);
      toast.success(data.email_sent ? "Invitation email sent" : "Invite created (email failed — share link manually)");
      loadInvites();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  const inviteStatus = (companyId) => {
    const inv = invites.find((i) => i.company_id === companyId && !i.accepted_at);
    if (inv) return { label: "Invited", color: "text-amber-700" };
    const acc = invites.find((i) => i.company_id === companyId && i.accepted_at);
    if (acc) return { label: "Portal active", color: "text-emerald-700" };
    return null;
  };

  return (
    <div data-testid="companies-page">
      <PageHeader
        subtitle="Onboarding"
        title="Client Companies"
        action={
          <div className="flex items-center gap-4">
            {isAdmin && (
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} data-testid="companies-show-deleted" />
                Show deleted
              </label>
            )}
            <button onClick={onNew} className="btn-primary" data-testid="add-company-btn">+ Add Company</button>
          </div>
        }
      />
      <TableShell
        columns={["Name", "Contact", "Email", "Phone", "Portal", "Actions"]}
        empty={rows.length === 0 ? "No client companies yet. Add your first one." : null}
      >
        {rows.map((r) => {
          const s = inviteStatus(r.id);
          const isDeleted = !!r.deleted_at;
          return (
            <tr key={r.id} className={`tbl-row border-t border-slate-100 ${isDeleted ? "opacity-60" : ""}`} data-testid={`company-row-${r.id}`}>
              <td className="px-4 py-2.5 font-medium">
                {r.name}
                {isDeleted && (
                  <span className="ml-2 inline-block bg-slate-100 text-slate-500 border border-slate-200 rounded-full px-2 py-0.5 text-[10px] align-middle">
                    deleted {fmtDeletedDate(r.deleted_at)}
                  </span>
                )}
              </td>
              <td className="px-4 py-2.5">{r.contact_name || <span className="text-slate-400">—</span>}</td>
              <td className="px-4 py-2.5 text-slate-600">{r.contact_email || <span className="text-slate-400">—</span>}</td>
              <td className="px-4 py-2.5 text-slate-600 font-mono text-xs">{r.phone || "—"}</td>
              <td className="px-4 py-2.5 text-xs">
                {s ? <span className={s.color + " font-medium"}>{s.label}</span> : <span className="text-slate-400">—</span>}
              </td>
              <td className="px-4 py-2.5 space-x-3 whitespace-nowrap">
                {isDeleted ? (
                  <button className="text-emerald-700 hover:underline text-xs" onClick={() => restore(r.id)} data-testid={`restore-${r.id}`}>Restore</button>
                ) : (
                  <>
                    <button className="text-[#005E82] hover:underline text-xs inline-flex items-center gap-1" onClick={() => openInvite(r)} data-testid={`invite-${r.id}`}>
                      <Mail size={12} /> Invite
                    </button>
                    <button className="text-[#005E82] hover:underline text-xs" onClick={() => onEdit(r)} data-testid={`edit-${r.id}`}>Edit</button>
                    <button className="text-rose-600 hover:underline text-xs" onClick={() => remove(r.id)} data-testid={`delete-${r.id}`}>Delete</button>
                  </>
                )}
              </td>
            </tr>
          );
        })}
      </TableShell>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title={editingId ? "Edit Company" : "New Company"}
        footer={
          <>
            <button onClick={() => setOpen(false)} className="text-sm px-3 py-2 rounded-md border border-slate-200">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-company-btn">{saving ? "Saving…" : "Save"}</button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Company name" testId="f-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Field label="Contact name" testId="f-contact" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
          <Field label="Contact email" testId="f-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
          <Field label="Phone" testId="f-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Field label="Address" testId="f-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </div>
      </Drawer>

      <Drawer
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title="Invite portal user"
        footer={
          inviteResult ? (
            <button onClick={() => setInviteOpen(false)} className="btn-primary" data-testid="close-invite-btn">Done</button>
          ) : (
            <>
              <button onClick={() => setInviteOpen(false)} className="text-sm px-3 py-2 rounded-md border border-slate-200">Cancel</button>
              <button onClick={sendInvite} className="btn-primary" data-testid="send-invite-btn">Send invite</button>
            </>
          )
        }
      >
        {!inviteResult ? (
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600">
              We'll email a one-time activation link. The user chooses their own password on the activation page. Nothing sensitive is sent by email.
            </div>
            <Field label="Recipient email" testId="i-email" type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} />
            <Field label="Recipient name (optional)" testId="i-name" value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} />
          </div>
        ) : (
          <div className="space-y-4">
            <div className={`p-3 rounded-md text-sm border ${inviteResult.email_sent ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
              {inviteResult.email_sent ? "Invitation email sent successfully." : `Email couldn't be sent (${inviteResult.email_error}). Share the link manually.`}
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Invite link</label>
              <div className="mt-1 flex items-center gap-2 bg-slate-900 text-slate-100 rounded-md px-3 py-2 font-mono text-xs">
                <span className="flex-1 truncate" data-testid="invite-link">{inviteResult.invite_link}</span>
                <button onClick={() => copy(inviteResult.invite_link)} className="text-emerald-300 hover:text-emerald-200 flex items-center gap-1"><Copy size={12} /> Copy</button>
              </div>
            </div>
            <div className="text-xs text-slate-500">Expires {new Date(inviteResult.expires_at).toLocaleDateString()}</div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
